/* eslint max-len: ["error", { "code": 120 }] */
import { createSelector } from "reselect";
import queryString from "query-string";
const parseQueryString = queryString.parse;
import { differenceInDays, determineComparisonDate } from "../utils/date";
import { isToday, isFuture, isBefore, isValid, compareAsc, parse, addDays } from "date-fns";

import { isDateWithin30days, getRowSlugs, sortCollection } from "./helpers";
import { fuseSearch } from "./search";

import {
  tabKeys,
  tabConfig,
  fieldConfig,
  allFieldKeys,
  icerBenchmarkPriceKeys,
  comparisonValuesKeys,
  SELECTED_ROW_STORAGE_KEY,
  computeHeaderProps,
  newItemPeriod,
  costDescriptions,
  costRatioFields,
} from "../config";

import { formatPercent } from "../utils/formatters";

export const activeDetailIdSelector = (state, props) => {
  // Support both React Router v5 (props.match.params) and v6 (props.params)
  const params = props.params || props.match?.params;
  if (!params) return null;
  return params.detailId;
};

export const activeTabKeySelector = (state, props) => {
  // Support both React Router v5 (props.match.params) and v6 (props.params)
  const params = props.params || props.match?.params;
  return !params ? tabKeys[0] : params.activeTabKey || tabKeys[0];
};

const detailsSlugSelector = (state, props) => {
  // Support both React Router v5 (props.match.params) and v6 (props.params)
  const params = props.params || props.match?.params;
  if (!params) return null;
  return params.detailsSlug;
};

export const searchStringSelector = (state, props) => {
  // Support both React Router v5 (props.location) and v6 (props.location)
  const location = props.location;
  if (!location) return "";
  const { search = "" } = location;
  const urlParams = parseQueryString(search);
  // Note difference between "search" as a react-router property, vs the URL param named "search":
  const { search: searchString = "" } = urlParams;
  return searchString;
};

const enabledGridFieldsSelector = (state) => state.enabledGridFields;
export const userInputSelector = (state) => state.userInputs.data;

export const analysisCollectionArraySelector = (state) => state.analysesResponse.data;

// Cache of analyses, by id:  (JSONAPI response is an Array)
export const analysisCollectionSelector = createSelector(
  [analysisCollectionArraySelector],
  (analyses) => Object.fromEntries(analyses.map((analysis) => [analysis.slug, analysis])),
);

export const getReportSnapshotCollection = (state, { limit = 0, searchTerm = "" } = {}) => {
  const { data = [] } = state.assetGroups;
  const collection = data.filter((record) => record && record.status !== "coming_soon");
  if (limit && limit > 0) return collection.slice(0, limit);
  if (searchTerm.length <= 2) {
    return collection;
  } else {
    return fuseSearch(searchTerm, {
      collection,
      keys: ["specific_conditions", "drugs"],
    });
  }
};

export const reportSnapshotSelector = (state, props) => {
  const reportId = props.reportId || activeDetailIdSelector(state, props);
  if (!reportId) return null;
  return getReportSnapshotCollection(state).find((r) => r.id === reportId);
};

export const getComingSoonCollection = (state, { limit = null } = {}) => {
  const { data = [] } = state.comingSoon;
  if (limit && limit > 0) return data.slice(0, limit);
  return data;
};

export const comingSoonLoadingSelector = (state) => state.comingSoon.loading;

// The asset group associated with a coming-soon resource. It used to be linked by
// JSON:API relationship into a sibling `included` array; GraphQL nests it on the
// record (#1083).
const assetGroupFor = (item) => item?.asset_group || null;

export const comingSoonMostRecentItemSelector = (state) => {
  const comingSoonItems = getComingSoonCollection(state);
  if (!comingSoonItems) return null;

  // Asset groups compete with the resources themselves for "soonest", on
  // date_of_review rather than display_date. `type` is what ComingSoonBanner
  // branches on to tell an assessment from a resource.
  const relatedObjects = comingSoonItems
    .map(assetGroupFor)
    .filter(Boolean)
    .map((assetGroup) => ({ ...assetGroup, type: "asset_group" }));

  const items = [...comingSoonItems, ...relatedObjects];
  const comingSoonMostRecentItem = items
    .filter((item) => {
      const resourceDate = parse(
        item.display_date || item.date_of_review || "",
        "yyyy-MM-dd",
        new Date(),
      );
      if (!isValid(resourceDate)) return false;
      return (
        isToday(resourceDate) ||
        (isFuture(resourceDate) && isBefore(resourceDate, addDays(new Date(), 30)))
      );
    })
    .sort((a, b) => {
      const resourceDateA = parse(
        a.display_date || a.date_of_review || "",
        "yyyy-MM-dd",
        new Date(),
      );
      const resourceDateB = parse(
        b.display_date || b.date_of_review || "",
        "yyyy-MM-dd",
        new Date(),
      );
      //return resourceDateA > resourceDateB;
      return compareAsc(resourceDateA, resourceDateB);
    })[0];

  return comingSoonMostRecentItem || null;
};

export const comingSoonSelector = (state, { limit = null } = {}) => {
  const collection = getComingSoonCollection(state, { limit });
  let resource = {};
  return collection.map((item) => {
    const report = assetGroupFor(item);
    // Previously assigned onto report.attributes, which mutated the object held in
    // Redux state. Computed locally instead.
    const reportIsNew = Boolean(
      report?.created_at && isDateWithin30days(new Date(report.created_at)),
    );
    const { drugs = [], specific_conditions = [], date_of_review: date } = report || {};

    if (item && Object.keys(item).length) {
      const {
        display_date,
        title: webinarTitle,
        registration_url: registrationUrl,
        youtube_id: videoId,
        description = "",
        created_at,
        // Excluded from the rest spread: it is the nested report, not a resource
        // field, and would otherwise land in `resource`.
        asset_group: _assetGroup,
        id: _id,
        ...otherWebinarAttrs
      } = item;

      // If there's an associated report, use that status, otherwise, check resource.
      // - If no report, than we can show the resource's isNew.
      const isNew = !report && isDateWithin30days(new Date(created_at));

      resource = {
        registrationUrl,
        title: webinarTitle,
        date: display_date,
        videoId,
        description,
        isNew,
        ...otherWebinarAttrs,
      };
    }
    const title = specific_conditions[0] || "";
    return {
      id: item.id,
      title,
      report: report ? { date, title, isNew: reportIsNew } : null,
      drugs,
      resource,
    };
  });
};

export const newComingSoonCountSelector = (state) => {
  let count = 0;
  comingSoonSelector(state).forEach((item) => {
    (item.resource?.isNew || item.report?.isNew) && count++;
  });
  return count;
};

export const analysesLoadingSelector = (state) => state.sortedAnalyses.loading;
// the ^above allows progess indicator to be visible while initial filter & sort are 1st applied, vs below:
// export const analysesLoadingSelector = (state) => state.analysesResponse.loading;

const analysisIdSelector = (state, ownProps) => ownProps.analysisId;

// returns callback function for Array.sort:
const fuseSearchSelector = createSelector(
  [searchStringSelector, analysisCollectionArraySelector], // every member is passed: state, props
  (searchString, collection) => fuseSearch(searchString, { collection }),
);

const sortedAnalysesSelector = createSelector(
  [analysisCollectionArraySelector, (state) => state.sortedAnalyses.sortedIds],
  sortCollection,
);

const sortedAndFilteredAnalysesSelector = createSelector(
  [fuseSearchSelector, (state) => state.sortedAnalyses.sortedIds, (state) => state.sort.sortField],
  sortCollection,
);

const addedToCompareCollectionSelector = createSelector(
  [
    userInputSelector,
    sortedAnalysesSelector,
    (state) => state.sortedAnalyses.sortedIds,
    (state) => state.sort.sortField,
  ],
  (userInputs, collection, sortedIds, sortField) => {
    const filteredCollection = collection.filter((row) => {
      const { [SELECTED_ROW_STORAGE_KEY]: selected = false } = (row && userInputs[row.slug]) || {};
      return selected;
    });
    return sortCollection(filteredCollection, sortedIds, sortField);
  },
);

const filteredRowsSelector = createSelector(
  [activeTabKeySelector, addedToCompareCollectionSelector, sortedAndFilteredAnalysesSelector],
  (activeTabKey, comparisonAnalyses, sortedAnalyses) => {
    if (activeTabKey === "compare-your-own-pricing") {
      return comparisonAnalyses;
    }
    return sortedAnalyses;
  },
);

const filteredRowsForAllTabsSelector = createSelector(
  [sortedAndFilteredAnalysesSelector, addedToCompareCollectionSelector],
  (sortedAnalyses, comparisonAnalyses) => ({
    [tabConfig["drugs"]]: getRowSlugs(sortedAnalyses),
    [tabConfig["compare-your-own-pricing"]]: getRowSlugs(comparisonAnalyses),
    [tabConfig["icer-pricing"]]: getRowSlugs(sortedAnalyses),
  }),
);

export const filteredAnalysisIdsSelector = createSelector(
  [filteredRowsSelector],
  (filteredAnalyses) => filteredAnalyses.map((analysis) => (analysis ? analysis.slug : null)),
);

export const filteredAnalysisIdsForAllTabsSelector = filteredRowsForAllTabsSelector;

export const columnConfigSelector = createSelector(
  [activeTabKeySelector, enabledGridFieldsSelector],
  (activeTabKey, enabledGridFields) => computeHeaderProps(activeTabKey, enabledGridFields), // eslint-disable-line max-len
);

export const dataRowPropsSelector = createSelector(
  [columnConfigSelector],
  ({ dataRowProps }) => dataRowProps,
);

export const columnCountSelector = createSelector(
  [dataRowPropsSelector],
  // 1st subrow corresponds to full # of columns despite colspans -- each can have rowSpans > 1 :
  (dataRowProps) => dataRowProps[0].columnProps.length,
);

const preloadedUserEnteredValues = {}; // empty object that will avoid mutation, across calls

export const userEnteredValuesByAnalysisIdSelector = createSelector(
  [userInputSelector, analysisIdSelector],
  (userInputs, analysisId) => {
    // until a value is set, userInputs[analysisId] is undefined:
    const { [analysisId]: userEnteredValuesForRow = preloadedUserEnteredValues } = userInputs;
    return userEnteredValuesForRow;
  },
);

export const analysisByIdSelector = createSelector(
  // id from ownProps -- e.g.: from one element of a collection
  [analysisCollectionSelector, analysisIdSelector],
  (analysisCollection, analysisId) => analysisCollection[analysisId],
);

export const analysisBySlugSelector = createSelector(
  // details id from URL path
  [analysisCollectionSelector, detailsSlugSelector],
  (analysisCollection, detailsSlug) => analysisCollection[detailsSlug],
);

export const analysesSelector = createSelector(
  // details id from URL path
  [analysisCollectionSelector],
  (analysisCollection) => analysisCollection,
);

const currentConditionSelector = createSelector(
  [analysisBySlugSelector],
  (analysis) => analysis && analysis.specific_condition,
);

const emptyArray = []; // avoid mutation
const analysesForConditionSelector = createSelector(
  [analysisCollectionSelector, currentConditionSelector],
  (analysisCollection, currentCondition) => {
    if (currentCondition) {
      // TODO: use id of condition relationship:
      return Object.values(analysisCollection).filter(
        (a) => a.specific_condition === currentCondition,
      );
    }
    return emptyArray;
  },
);

export const fieldsForExportSelector = createSelector(
  [enabledGridFieldsSelector],
  (enabledGridFields) => {
    const fieldsForTabs = {};
    tabKeys.forEach((key) => {
      const tabIndex = tabKeys.indexOf(key);
      fieldsForTabs[tabConfig[key]] = allFieldKeys
        // filter by tab:
        .filter((k) => fieldConfig[k].tabFilters[tabIndex] === 1)
        .filter((k) => !fieldConfig[k].fixedValue && k !== "remove_from_compare")
        .filter((k) =>
          (fieldConfig[k].filters || []).reduce((accVisible, filterKey) => {
            return accVisible && enabledGridFields[filterKey] === true;
          }, true),
        );
    });
    return fieldsForTabs;
  },
);
export const relatedDrugsForConditionSelector = createSelector(
  [analysesForConditionSelector],
  (analysesForCondition) => {
    // dedupe:
    const drugs = {};
    const drugsForCondition = analysesForCondition.reduce((acc, a) => {
      if (!drugs[a.trade_name]) {
        acc.push(a.trade_name);
        drugs[a.trade_name] = true;
      }
      return acc;
    }, []);
    return drugsForCondition.length ? drugsForCondition : emptyArray;
  },
);

export const addToCompareCountSelector = createSelector(
  [addedToCompareCollectionSelector],
  (comparisonAnalyses) => comparisonAnalyses.length,
);

export const computePercentDiscount = (benchmarkPrice, comparisonPrice, comparisonKey) => {
  if (!Number.isFinite(benchmarkPrice)) {
    return null; // becomes formatted as <small>N/A</small> via formatValue
  }
  // do not divide by 0 -- test for comparisonPrice:
  if (Number.isFinite(comparisonPrice) && comparisonPrice) {
    return (100 * (benchmarkPrice - comparisonPrice)) / comparisonPrice;
  }
  if (comparisonKey === "user_proposed_price") {
    // So far, there's no table-cell formatting in these selectors, except for here?
    return "--"; // it is computable, b/c benchmarkPrice exists -- but comparisonPrice was not entered
  }
  return null;
};

const getDerivedPercentDiscount = (
  benchmarkKey,
  comparisonKey,
  userEnteredValuesByAnalysisId,
  analysisById,
) => {
  if (comparisonKey === "user_proposed_price") {
    const benchmarkPrice = analysisById[benchmarkKey];
    const comparisonPrice = parseFloat(userEnteredValuesByAnalysisId[comparisonKey], 10);
    return computePercentDiscount(benchmarkPrice, comparisonPrice, comparisonKey);
  }
  return analysisById.comparisonValues[benchmarkKey][comparisonKey];
};

// Used only by browser/table view, where `fieldKey` is passed via ownProps:
const benchmarkKeySelector = (state, ownProps) => fieldConfig[ownProps.fieldKey].benchmarkKey;
const comparisonKeySelector = (state, ownProps) => fieldConfig[ownProps.fieldKey].comparisonKey;
export const priceDiscountPercentSelector = createSelector(
  [
    benchmarkKeySelector,
    comparisonKeySelector,
    userEnteredValuesByAnalysisIdSelector,
    analysisByIdSelector,
  ],
  getDerivedPercentDiscount,
);

// Used only by CSV-download feature:
const formatCsvItem = (k, value) => {
  // This formatting is different from the Browser table view, e.g.: raw $USD values
  const { type } = fieldConfig[k];
  switch (type) {
    case "percent": {
      if (Number.isFinite(value)) {
        // TODO: move this into formatPercent?
        return formatPercent(value);
      }
    }
    // eslint-disable-next-line no-fallthrough
    case "usd": {
      // keep usd as raw number, without $ or commas
      const isRatioField = costRatioFields.includes(k);
      if (isRatioField && value == -1) {
        return costDescriptions[0];
      } else if (isRatioField && value == -2) {
        return costDescriptions[1];
      } else if (isRatioField && value == -3) {
        return costDescriptions[2];
      } else if (isRatioField && value < 0) {
        return "N/A";
      } else {
        return value;
      }
    }
    case "boolean": // e.g.: format as "yes" vs "no"?
      return value ? "Yes" : "No";
    case "enum": {
      if (value && typeof value == "object") {
        return value.join && value.join(", ");
      }
    }
    // eslint-disable-next-line no-fallthrough
    case "string":
    default:
      return value;
  }
};
const getDerivedValueForFieldKey = (k, a, userInputForAnalysis = {}) => {
  if (typeof a[k] === "undefined") {
    // TODO: flatten all derived, comparison fieldKeys, into each analysis?
    if (comparisonValuesKeys.includes(k)) {
      // comparison keys make use of a more nested structure:
      const { benchmarkKey, comparisonKey } = fieldConfig[k];
      return getDerivedPercentDiscount(benchmarkKey, comparisonKey, userInputForAnalysis, a);
    }
    // TODO: flatten all user-input-related fieldKeys, into each analysis?
    if (["add_to_compare", "user_proposed_price"].includes(k)) {
      return userInputForAnalysis[k];
    }
    console.error("FIELD KEY NOT SUPPORTED: ", k);
  }
  return a[k];
};
// invoked on-the-fly, not via Component props:
export const csvArraySelector = createSelector(
  [
    (rowIds, visibleDataFields, analysisCollection, userInputs) => [
      rowIds,
      visibleDataFields,
      analysisCollection,
      userInputs,
    ],
  ],
  ([rowIds, visibleDataFields, analysisCollection, userInputs]) =>
    rowIds.map((analysisId) => {
      const a = analysisCollection[analysisId];
      const userInputForAnalysis = userInputs[analysisId];
      const csvRow = visibleDataFields.map((k) =>
        formatCsvItem(k, getDerivedValueForFieldKey(k, a, userInputForAnalysis)),
      );
      return csvRow;
    }),
);

const rowIdsSelector = (state, ownProps) => ownProps.rowIds;

// const chartComparisonKeys = ['50k_per_qaly', '100k_per_qaly', '150k_per_qaly', '200k_per_qaly'];
// chartDataSelector could return Array of analyses, but, maybe not?
export const chartDataSelector = createSelector(
  [rowIdsSelector, analysisCollectionSelector, userInputSelector],
  (rowIds, analysisCollection, userInputs) => {
    const chartData = rowIds
      .map((id) => analysisCollection[id])
      .map(({ slug: id, trade_name, specific_condition, differentiator }) => ({
        id,
        trade_name,
        specific_condition,
        differentiator,
        ...icerBenchmarkPriceKeys.reduce(
          (acc, k) => ({ ...acc, [k]: analysisCollection[id][k] }),
          {},
        ),
        // TODO: user_proposed_price is null or parseFloat, upstream?
        user_proposed_price:
          parseFloat(userInputs[id] && userInputs[id].user_proposed_price, 10) || null,
      }));
    return chartData;
  },
);

const mediaResourcesDataSelector = (state) => state.mediaResources.data;
const recentUpdatesDataSelector = (state) => state.recentUpdates.data;

const getPublished = (items) => items.filter(({ unpublished }) => !unpublished);
const getResourceType = (items, type) =>
  items.filter(({ resource_type }) => resource_type === type);

const newCount = (items = []) => {
  const today = new Date();
  return items.filter((item) => {
    const { created_at, display_date } = item;
    const comparisonDate = determineComparisonDate(created_at, display_date);
    return differenceInDays([comparisonDate, today]) < newItemPeriod;
  }).length;
};

const newResourceCountSelector = (type) =>
  createSelector([mediaResourcesDataSelector], (mediaResources) =>
    newCount(getResourceType(getPublished(mediaResources), type)),
  );

export const newWebinarsCountSelector = newResourceCountSelector("webinar");
export const newVideosCountSelector = newResourceCountSelector("resource");
export const newDocumentsCountSelector = newResourceCountSelector("document");
export const newRecentUpdatesCountSelector = createSelector(
  [recentUpdatesDataSelector],
  (recentUpdates) => newCount(getPublished(recentUpdates)),
);
